import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FilterGroup } from '../components/hexagon/HexFilterBar';
import type { HexAlert } from '../api/contracts';

/**
 * "Show me only the cameras carrying THIS alert" — the one filter all three alert boards share.
 * The boards reach it through `useBoardFilters`, which adds severity and "only open" on top.
 *
 * WHY IT IS A HOOK AND NOT THREE COPIES
 * ────────────────────────────────────
 * The honeycomb, the GIS plan and the geographic map are three drawings of one estate, and an
 * operator who filters to "Fire" on one and flips to another expects the same question to
 * still be asked. Three implementations would answer it three ways the first time any of them
 * learned something — which alerts count as active, how a name is derived, what happens when
 * the last one clears — and the divergence would look like a broken board rather than a bug.
 *
 * WHAT IT DOES NOT DO
 * ───────────────────
 * It DIMS; it never hides. A board an operator has learned the shape of must not reflow under
 * a filter: the hexagon they know, the pin they remember at the top of the yard, both stay
 * where they are and the misses grey out. That is also why the count is reported back — a
 * dimmed camera and a camera with nothing happening look identical, so the bar has to say in
 * words how many survived.
 */

/** The "no restriction" chip key. Shared with the boards' other groups. */
export const ALL_ALERTS = '__all__';

export interface AlertTypeFilter {
  /** The selected alert name, or ALL_ALERTS. */
  value: string;
  setValue: (key: string) => void;
  /** Each active alert name and how many in-scope cameras carry it, busiest first. */
  options: Array<{ name: string; count: number }>;
  /** True when this camera should be greyed out under the current choice. */
  isDimmed: (cameraId: string) => boolean;
  /**
   * Does this ALERT match the chosen type? True whenever nothing is chosen.
   *
   * Per alert, not per camera, and the difference shows on a camera carrying two kinds at
   * once: under "Fire" its pin stays lit — it does have a fire — but the panel beside it
   * should list the fire and not the loitering. Filtering the panel by camera would have put
   * both back, which is the board answering a question that was not asked.
   */
  matches: (alert: Pick<HexAlert, 'alertName' | 'eventType'>) => boolean;
  /** How many in-scope cameras survive — what the bar reports. */
  matchCount: number;
  /** Ready to drop into `HexFilterBar`'s `groups`. */
  group: FilterGroup;
  /** Whether anything is actually being filtered — for callers that hide an empty bar. */
  active: boolean;
}

export interface UseAlertTypeFilterOptions {
  /** The board's recent alerts and its active cards — both, because either can name a camera. */
  recent: HexAlert[];
  cards: HexAlert[];
  /** The cameras this board draws. Counts and the match total are relative to these. */
  cameraIds: string[];
  /** Plural noun for the result line — "cameras" on the honeycomb, "pins" on the maps. */
  noun?: string;
}

export function useAlertTypeFilter({
  recent,
  cards,
  cameraIds,
}: UseAlertTypeFilterOptions): AlertTypeFilter {
  const [value, setValue] = useState<string>(ALL_ALERTS);

  /** Which alert names each camera is currently carrying. */
  const namesByCamera = useMemo(() => {
    const m = new Map<string, Set<string>>();
    const add = (a: HexAlert) => {
      // `alertName` is the decision's own name ("Blacklisted Person"); `eventType` is the
      // fallback for rows written before alerts carried one. An alert with neither names
      // nothing an operator could pick, so it is not offered.
      const name = (a.alertName || a.eventType || '').trim();
      if (!a.cameraId || !name) return;
      const hit = m.get(a.cameraId);
      if (hit) hit.add(name);
      else m.set(a.cameraId, new Set([name]));
    };
    recent.forEach(add);
    cards.forEach(add);
    return m;
  }, [recent, cards]);

  const options = useMemo(() => {
    const counts = new Map<string, number>();
    cameraIds.forEach((id) => {
      namesByCamera.get(id)?.forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
    });
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [cameraIds, namesByCamera]);

  // Alerts clear as operators work them. A filter left pointing at a name that no longer
  // fires would silently grey the whole board out, so it releases itself.
  useEffect(() => {
    if (value !== ALL_ALERTS && !options.some((o) => o.name === value)) setValue(ALL_ALERTS);
  }, [options, value]);

  const isDimmed = useCallback(
    (cameraId: string) => value !== ALL_ALERTS && !namesByCamera.get(cameraId)?.has(value),
    [namesByCamera, value],
  );

  const matches = useCallback(
    (alert: Pick<HexAlert, 'alertName' | 'eventType'>) =>
      value === ALL_ALERTS || (alert.alertName || alert.eventType || '').trim() === value,
    [value],
  );

  const alertingCount = useMemo(
    () => cameraIds.reduce((n, id) => (namesByCamera.has(id) ? n + 1 : n), 0),
    [cameraIds, namesByCamera],
  );

  const matchCount = useMemo(
    () => cameraIds.reduce((n, id) => (isDimmed(id) ? n : n + 1), 0),
    [cameraIds, isDimmed],
  );

  const group = useMemo<FilterGroup>(() => ({
    label: 'Alert type',
    value,
    onChange: setValue,
    chips: [
      { key: ALL_ALERTS, label: 'All alerts', count: alertingCount },
      ...options.map((o) => ({
        key: o.name,
        label: o.name,
        count: o.count,
        title: `Show only cameras with a "${o.name}" alert — ${o.count} camera${o.count === 1 ? '' : 's'}`,
      })),
    ],
  }), [alertingCount, options, value]);

  return { value, setValue, options, isDimmed, matches, matchCount, group, active: value !== ALL_ALERTS };
}

export default useAlertTypeFilter;
